import {
  ArrowLeft,
  Calendar,
  MapPin,
  Users,
  Clock,
  Search,
  Star,
  Plus,
  Grid,
  List,
  TrendingUp,
} from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { EventService } from "../services/APIs";
import { Event } from "../utils/types/event";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";
import event1 from "../assets/event-1.jpg";
import event2 from "../assets/event-2.jpg";
import event3 from "../assets/event-3.jpg";

// Helper function to format date
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

// Helper function to format time
const formatTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Helper function to format price
const formatPrice = (eventPrice: string): string => {
  if (eventPrice === "free") {
    return "Free";
  }
  if (eventPrice === "paid") {
    return "Paid";
  }
  return "Paid";
};

const Events = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        // Get organization_id from localStorage
        const organizationId = localStorage.getItem("OrganizationId");

        // Determine event filter based on active tab
        const eventFilter = activeTab === "upcoming" ? "upcoming" : "past";

        // Fetch events filtered by organization_id and event type
        const data = await EventService.getAllEvents(false, {
          organization_id: organizationId ?? undefined,
          event: eventFilter,
        });
        setEvents(data || []);
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [activeTab]);


  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Volunteering":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "Fundraising":
        return "bg-purple-500/10 text-purple-600 border-purple-500/30";
      case "Education":
        return "bg-blue-500/10 text-blue-600 border-blue-500/30";
      case "Training":
        return "bg-amber-500/10 text-amber-600 border-amber-500/30";
      case "Charity":
        return "bg-red-500/10 text-red-600 border-red-500/30";
      case "Community":
        return "bg-teal-500/10 text-teal-600 border-teal-500/30";
      default:
        return "bg-muted text-muted-foreground border-border/40";
    }
  };

  const filteredEvents = events.filter(event => {
    const search = searchQuery.toLowerCase();
    const matchesSearch =
      event.event_name.toLowerCase().includes(search) ||
      (event.address ?? "").toLowerCase().includes(search) ||
      (event.event_type ?? "").toLowerCase().includes(search);

    return matchesSearch;
  });


  return (
    <div className="min-h-screen bg-background pb-20 md:pb-8">
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary" />
                <h1 className="text-lg font-bold text-primary md:text-xl">
                  Events
                </h1>
              </div>
              <p className="text-xs text-muted-foreground">
                Discover organization events
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search events..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-10 h-11 bg-muted/50"
              />
            </div>
            <div className="flex gap-2">
              <div className="hidden md:flex border rounded-lg">
                <Button
                  variant={viewMode === "grid" ? "secondary" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className="rounded-r-none"
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === "list" ? "secondary" : "ghost"}
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <Tabs
          defaultValue="upcoming"
          className="w-full"
          value={activeTab}
          onValueChange={setActiveTab}
        >
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6">
            <TabsTrigger value="upcoming">
              Upcoming
              <Badge variant="secondary" className="ml-2">
                {activeTab === "upcoming" ? filteredEvents.length : 0}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="past">Past Events</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Loading events...</p>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No events found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                    : "space-y-4"
                }
              >
                {filteredEvents.map(event => {
                  const imageUrl =
                    event.event_banner && event.event_banner.length > 0
                      ? event.event_banner[0]
                      : event2;
                  const eventDate = formatDate(event.start_date);
                  const eventTime = formatTime(event.start_date);
                  const category =
                    event.event_type ?? formatPrice(event.event_price);
                  const attendees = event.interested_count ?? 0;

                  return (
                    <Card
                      key={event.id}
                      className={`cursor-pointer transition-all hover:shadow-lg overflow-hidden ${
                        viewMode === "list"
                          ? "flex flex-row"
                          : "hover:-translate-y-1"
                      }`}
                      onClick={() =>
                        navigate(
                          `${CitycollabNavigationRoutes.EVENTS}/${event.id}`
                        )
                      }
                    >
                      <div
                        className={
                          viewMode === "list"
                            ? "w-48 h-auto shrink-0 overflow-hidden"
                            : "aspect-video w-full overflow-hidden"
                        }
                      >
                        <img
                          src={imageUrl}
                          alt={event.event_name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className={viewMode === "list" ? "flex-1 p-4" : ""}>
                        <CardHeader
                          className={viewMode === "list" ? "p-0 pb-2" : ""}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <Badge className={getCategoryColor(category)}>
                              {category}
                            </Badge>
                            {attendees >= 0.8 * (event.max_attendees ?? 100) && (
                              <Badge className="bg-red-500/10 text-red-600 border-red-500/30 text-xs">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                Filling up!
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg">
                            {event.event_name}
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {event.organizer ?? "Organization event"}
                          </CardDescription>
                        </CardHeader>
                        <CardContent
                          className={
                            viewMode === "list" ? "p-0" : "space-y-2"
                          }
                        >
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>{eventDate}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>{eventTime}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <MapPin className="h-4 w-4" />
                            <span className="truncate">
                              {event.address ?? "Location not specified"}
                            </span>
                          </div>
                          <div className="flex items-center justify-between pt-2">
                            <div className="flex items-center gap-2 text-sm text-primary font-medium">
                              <Users className="h-4 w-4" />
                              <span>
                                {attendees} attending
                                {event.max_attendees
                                  ? ` / ${event.max_attendees}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {loading ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Loading events...</p>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">
                  No past events found
                </h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filter criteria
                </p>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid gap-4 md:grid-cols-2 lg:grid-cols-3"
                    : "space-y-4"
                }
              >
                {filteredEvents.map(event => {
                  const imageUrl =
                    event.event_banner && event.event_banner.length > 0
                      ? event.event_banner[0]
                      : event3;
                  const eventDate = formatDate(event.start_date);
                  const attendees = event.interested_count ?? 0;
                  const category =
                    event.event_type ?? formatPrice(event.event_price);

                  return (
                    <Card
                      key={event.id}
                      className={`opacity-75 cursor-pointer transition-all hover:shadow-lg overflow-hidden ${
                        viewMode === "list"
                          ? "flex flex-row"
                          : "hover:-translate-y-1"
                      }`}
                      onClick={() =>
                        navigate(
                          `${CitycollabNavigationRoutes.EVENTS}/${event.id}`
                        )
                      }
                    >
                      <div
                        className={
                          viewMode === "list"
                            ? "w-48 h-auto shrink-0 overflow-hidden"
                            : "aspect-video w-full overflow-hidden"
                        }
                      >
                        <img
                          src={imageUrl}
                          alt={event.event_name}
                          className="w-full h-full object-cover grayscale"
                        />
                      </div>
                      <div className={viewMode === "list" ? "flex-1 p-4" : ""}>
                        <CardHeader
                          className={viewMode === "list" ? "p-0 pb-2" : ""}
                        >
                          <div className="mb-2">
                            <Badge variant="outline">{category}</Badge>
                          </div>
                          <CardTitle className="text-lg">
                            {event.event_name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent
                          className={
                            viewMode === "list" ? "p-0" : "space-y-2"
                          }
                        >
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>{eventDate}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{attendees} attended</span>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Events;
